import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../../lib/supabaseClient";
import type { ConversationMessage, ConversationThreadView } from "../../../store/types";
import PhotographerLayout from "../components/PhotographerLayout";
import { usePhotographerAuth } from "../../../contexts/PhotographerAuthContext";

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

interface Itinerary { meeting_point: string; meeting_time: string | null; map_url: string; notes: string; }

/** Detect if a message is a dispatch-originated system notice */
function isDispatchNotice(msg: ConversationMessage): boolean {
  return (
    msg.message_type === "system_notice" &&
    (msg.sender_type === "system" ||
      (msg.sender_type === "admin" && msg.sender_id === "system"))
  );
}

export default function PhotographerMessagesPage() {
  const { session } = usePhotographerAuth();
  const [threads, setThreads] = useState<ConversationThreadView[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [dismissedNotice, setDismissedNotice] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const selectedThread = threads.find((t) => t.id === selectedId) ?? null;

  const fetchThreads = useCallback(async () => {
    if (!supabase || !session) return;
    setLoadingThreads(true);
    const { data: confs } = await supabase.from("booking_confirmations").select("order_id").eq("photographer_id", session.photographer_id);
    const orderIds = (confs ?? []).map((c: { order_id: string }) => c.order_id).filter(Boolean);
    if (orderIds.length === 0) { setThreads([]); setLoadingThreads(false); return; }

    const { data } = await supabase
      .from("conversation_threads")
      .select("*, orders(order_number, customer_name, shoot_date, selected_city)")
      .in("order_id", orderIds)
      .eq("participant_type", "photographer")
      .order("updated_at", { ascending: false });

    if (data) {
      setThreads((data as Record<string, unknown>[]).map((row) => {
        const order = row.orders as Record<string, string> | null;
        return {
          id: row.id as string,
          order_id: row.order_id as string | null,
          participant_type: row.participant_type as ConversationThreadView["participant_type"],
          subject: row.subject as string,
          status: row.status as ConversationThreadView["status"],
          risk_level: (row.risk_level as ConversationThreadView["risk_level"]) ?? "none",
          risk_summary: (row.risk_summary as string) ?? "",
          created_at: row.created_at as string,
          updated_at: row.updated_at as string,
          order_number: order?.order_number,
          customer_name: "客戶（平台聯繫）",
          shoot_date: order?.shoot_date,
          selected_city: order?.selected_city,
        };
      }));
    }
    setLoadingThreads(false);
  }, [session]);

  const fetchMessages = useCallback(async (threadId: string) => {
    if (!supabase) return;
    setLoadingMessages(true);
    const { data } = await supabase.from("conversation_messages").select("*").eq("thread_id", threadId).order("created_at", { ascending: true });
    if (data) setMessages(data as ConversationMessage[]);
    await supabase.from("conversation_messages").update({ is_read_photographer: true }).eq("thread_id", threadId).eq("is_read_photographer", false);
    setLoadingMessages(false);
  }, []);

  const fetchItinerary = useCallback(async (orderId: string | null) => {
    if (!supabase || !orderId) { setItinerary(null); return; }
    const { data } = await supabase.from("itineraries").select("*").eq("order_id", orderId).maybeSingle();
    setItinerary(data as Itinerary | null);
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  useEffect(() => {
    if (!selectedId) { setMessages([]); setItinerary(null); return; }
    fetchMessages(selectedId);
    const thread = threads.find((t) => t.id === selectedId);
    if (thread?.order_id) fetchItinerary(thread.order_id);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchMessages(selectedId), 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedId, fetchMessages, fetchItinerary, threads]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || !selectedId || sending) return;
    setSending(true);
    await supabase?.from("conversation_messages").insert([{
      thread_id: selectedId, sender_type: "photographer",
      sender_id: session?.photographer_id ?? "photographer",
      body: trimmed, message_type: "text", is_read_photographer: true,
    }]);
    await supabase?.from("conversation_threads").update({ updated_at: new Date().toISOString() }).eq("id", selectedId);
    setInput("");
    setSending(false);
    await fetchMessages(selectedId);
  };

  const SENDER_COLOR: Record<string, string> = {
    admin: "bg-[#2C2C2C] text-white",
    photographer: "bg-amber-100 text-amber-800",
    customer: "bg-emerald-100 text-emerald-800",
    system: "bg-teal-100 text-teal-700",
  };
  const SENDER_LABEL: Record<string, string> = {
    admin: "平台運營",
    photographer: "我",
    customer: "平台訊息",
    system: "排班通知",
  };

  // Latest unread dispatch notice for the selected thread
  const latestDispatchNotice = messages.findLast?.(isDispatchNotice) ?? messages.filter(isDispatchNotice).at(-1);
  const showDispatchCallout = latestDispatchNotice && dismissedNotice !== latestDispatchNotice.id;

  return (
    <PhotographerLayout title="">
      {/* Platform compliance warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-start gap-3">
        <div className="w-4 h-4 flex items-center justify-center text-amber-500 mt-0.5 shrink-0"><i className="ri-alert-line text-sm" /></div>
        <div className="text-xs font-light text-amber-800 leading-relaxed">
          <strong className="font-medium">重要提醒：</strong>
          請勿在平台外收取任何費用 · 請勿提供額外服務 · 所有客戶聯繫僅限透過此平台進行 · 請勿分享個人聯絡方式
        </div>
      </div>

      {/* Dispatch notice callout — prominent banner for newly assigned order */}
      {showDispatchCallout && (
        <div className="mb-4 bg-teal-50 border border-teal-200 rounded-xl px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-100 text-teal-600 shrink-0">
                <i className="ri-calendar-check-line text-base" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-teal-800">新攝影服務排班通知</p>
                  <span className="px-1.5 py-0.5 rounded bg-teal-200 text-teal-700 text-[9px] font-medium whitespace-nowrap">排班系統</span>
                </div>
                <p className="text-xs text-teal-700/80 font-light leading-relaxed mb-2">{latestDispatchNotice.body}</p>
                {selectedThread && (
                  <div className="flex flex-wrap gap-2">
                    {selectedThread.order_number && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-teal-200 text-teal-700 text-[10px] font-light">
                        訂單 {selectedThread.order_number}
                      </span>
                    )}
                    {selectedThread.selected_city && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-teal-200 text-teal-700 text-[10px] font-light">
                        <i className="ri-map-pin-line mr-1 text-[9px]" />{selectedThread.selected_city}
                      </span>
                    )}
                    {selectedThread.shoot_date && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-teal-200 text-teal-700 text-[10px] font-light">
                        <i className="ri-calendar-line mr-1 text-[9px]" />{selectedThread.shoot_date}
                      </span>
                    )}
                    {itinerary?.meeting_point && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-teal-200 text-teal-700 text-[10px] font-light">
                        <i className="ri-walk-line mr-1 text-[9px]" />集合：{itinerary.meeting_point}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setDismissedNotice(latestDispatchNotice.id)}
              className="w-5 h-5 flex items-center justify-center text-teal-400 hover:text-teal-600 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-4 h-[calc(100vh-280px)] min-h-96">
        {/* Thread list */}
        <div className="w-64 shrink-0 flex flex-col bg-white rounded-xl border border-[#E8E0D5] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F0EAE2]">
            <h3 className="text-xs font-medium text-[#2C2C2C] tracking-wide">指派訊息</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingThreads ? (
              <div className="flex justify-center py-8"><p className="text-[11px] text-[#2C2C2C]/30">載入中...</p></div>
            ) : threads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/15 mb-2"><i className="ri-message-3-line text-xl" /></div>
                <p className="text-[11px] text-[#2C2C2C]/30 font-light">尚無指派訊息</p>
              </div>
            ) : threads.map((t) => (
              <button key={t.id} onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-4 py-3 border-b border-[#F7F6F3] transition-colors cursor-pointer ${selectedId === t.id ? "bg-[#F7F6F3]" : "hover:bg-[#FAFAF8]"}`}>
                <div className="flex items-start justify-between gap-1 mb-1">
                  <span className="text-xs font-medium text-[#2C2C2C] truncate">{t.subject}</span>
                  {t.risk_level !== "none" && <span className="text-[9px] text-red-400 shrink-0">⚑</span>}
                </div>
                {t.order_number && <p className="text-[10px] text-[#2C2C2C]/40 mb-0.5">{t.order_number} · {t.selected_city}</p>}
                {t.shoot_date && <p className="text-[10px] text-[#2C2C2C]/35">{t.shoot_date}</p>}
              </button>
            ))}
          </div>
        </div>

        {/* Message panel */}
        <div className="flex-1 min-w-0 flex flex-col bg-white rounded-xl border border-[#E8E0D5] overflow-hidden">
          {!selectedThread ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <div className="w-9 h-9 flex items-center justify-center text-[#2C2C2C]/15 mb-2"><i className="ri-chat-3-line text-3xl" /></div>
              <p className="text-sm text-[#2C2C2C]/30 font-light">選擇左側訊息串</p>
            </div>
          ) : (
            <>
              {/* Order + meeting info header */}
              <div className="px-4 py-3 border-b border-[#F0EAE2]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-[#2C2C2C]">{selectedThread.subject}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-light whitespace-nowrap">攝影服務</span>
                </div>
                <p className="text-[10px] text-[#2C2C2C]/40 mb-2">{selectedThread.order_number} · {selectedThread.selected_city} · {selectedThread.shoot_date}</p>
                {itinerary ? (
                  <div className="bg-[#F7F6F3] rounded-lg px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <div>
                      <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">集合地點</p>
                      <p className="text-[10px] text-[#2C2C2C]/70 font-light">{itinerary.meeting_point || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">集合時間</p>
                      <p className="text-[10px] text-[#2C2C2C]/70 font-light">
                        {itinerary.meeting_time
                          ? new Date(itinerary.meeting_time).toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </p>
                    </div>
                    {itinerary.map_url && (
                      <div className="col-span-2">
                        <a href={itinerary.map_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#C8B8A6] underline cursor-pointer">查看地圖</a>
                      </div>
                    )}
                    {itinerary.notes && (
                      <div className="col-span-2">
                        <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">備註</p>
                        <p className="text-[10px] text-[#2C2C2C]/60 font-light">{itinerary.notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#F7F6F3] rounded-lg px-3 py-2">
                    <p className="text-[10px] text-[#2C2C2C]/40 font-light italic">集合資訊確認中，請等待平台通知</p>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-[#F0EAE2] rounded-lg">
                  <div className="w-3.5 h-3.5 flex items-center justify-center text-[#C8B8A6] shrink-0"><i className="ri-lock-line text-xs" /></div>
                  <p className="text-[10px] text-[#8B7355] font-light">客戶聯繫方式受到保護 — 請透過本平台進行所有溝通</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {loadingMessages ? (
                  <div className="flex justify-center py-6"><p className="text-xs text-[#2C2C2C]/30">載入中...</p></div>
                ) : messages.length === 0 ? (
                  <div className="flex justify-center py-6"><p className="text-xs text-[#2C2C2C]/25 font-light">尚無訊息</p></div>
                ) : messages.map((msg) => {
                  const isMe = msg.sender_type === "photographer";
                  const hasBlock = msg.is_blocked;
                  const isDispatch = isDispatchNotice(msg);
                  return (
                    <div key={msg.id} className={`flex flex-col gap-1 ${isMe ? "items-end" : "items-start"}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-light ${SENDER_COLOR[msg.sender_type] || "bg-gray-100 text-gray-500"}`}>
                          {isDispatch ? "排班通知" : (SENDER_LABEL[msg.sender_type] || msg.sender_type)}
                        </span>
                        <span className="text-[9px] text-[#2C2C2C]/25">{formatTime(msg.created_at)}</span>
                        {hasBlock && <span className="text-[9px] text-red-400">⊘ 已封鎖</span>}
                      </div>
                      <div className={`max-w-xs px-3 py-2 rounded-lg text-xs font-light leading-relaxed ${
                        hasBlock ? "bg-red-50 border border-red-200 text-red-400 line-through" :
                        isDispatch ? "bg-teal-50 border border-teal-200 text-teal-800 italic" :
                        msg.message_type === "system_notice" ? "bg-[#F0EAE2] border border-[#D9C9B4] text-[#8B7355] italic" :
                        isMe ? "bg-amber-50 border border-amber-100 text-[#2C2C2C]" : "bg-[#2C2C2C] text-white"
                      }`}>{msg.body}</div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="border-t border-[#F0EAE2] px-4 py-3 flex gap-2">
                <textarea value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(); }}
                  placeholder="透過平台傳送訊息給運營... (Ctrl+Enter)" rows={2}
                  disabled={selectedThread.status === "closed"}
                  className="flex-1 px-3 py-2 text-xs font-light border border-[#E8E0D5] rounded-lg bg-[#F7F6F3] text-[#2C2C2C] placeholder-[#2C2C2C]/30 focus:outline-none focus:border-[#C8B8A6] resize-none disabled:opacity-40 text-sm" />
                <button onClick={handleSend} disabled={!input.trim() || sending || selectedThread.status === "closed"}
                  className="self-end w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500 text-white disabled:opacity-40 hover:bg-amber-600 transition-colors cursor-pointer shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-fill text-sm" /></div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </PhotographerLayout>
  );
}
